import { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "../../context/TranslationContext";
import { ModalWrapper, ModalHeader, ModalBody, ModalFooter, FormError, InfoBox, Button } from "../common";
import toast from "react-hot-toast";
import { changePassword } from "../../lib/api/auth";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import { PasswordCriteria } from "./PasswordCriteria";
import { PasswordMatchIndicator } from "./PasswordMatchIndicator";

interface PasswordChangeModalProps {
  onClose: () => void;
  forceChange?: boolean;
}

export function PasswordChangeModal({ onClose, forceChange = false }: PasswordChangeModalProps) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const validation = usePasswordValidation(newPassword);

  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0 && confirmPassword.length > 0;
  const isFormValid = validation.allValid && passwordsMatch && currentPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validation.allValid) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDontMatch'));
      return;
    }

    setIsLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      toast.success(t('auth.passwordChanged'));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.passwordChangeFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper data-tsx-id="password-change-modal" onClose={forceChange ? undefined : onClose} size="md" closeOnClickOutside={!forceChange} closeOnEscape={!forceChange}>
      <ModalHeader
        icon={<Lock />}
        title={forceChange ? t('auth.mustChangePassword') : t('auth.changePassword')}
        onClose={forceChange ? undefined : onClose}
      />

      <ModalBody>
        {forceChange && (
          <InfoBox variant="warning" className="mb-4">
            {t('auth.mustChangePasswordMessage')}
          </InfoBox>
        )}

        <form id="password-change-form" onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <FormError>{error}</FormError>
          )}

          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-zinc-700 mb-1">
              {t('auth.currentPassword')}
            </label>
            <div className="relative">
              <input
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-2 pr-10 border border-zinc-300 placeholder-zinc-500 text-zinc-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder={t('auth.currentPassword')}
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-zinc-700 mb-1">
              {t('auth.newPassword')}
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-2 pr-10 border border-zinc-300 placeholder-zinc-500 text-zinc-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder={t('auth.newPassword')}
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <PasswordCriteria password={newPassword} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-700 mb-1">
              {t('auth.confirmPassword')}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-2 pr-10 border border-zinc-300 placeholder-zinc-500 text-zinc-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder={t('auth.confirmPassword')}
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <PasswordMatchIndicator 
              password={newPassword} 
              confirmPassword={confirmPassword} 
              showIndicator={true}
            />
          </div>
        </form>
      </ModalBody>

      <ModalFooter alignment="right">
        {!forceChange && (
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          form="password-change-form"
          variant="primary"
          disabled={isLoading || !isFormValid}
          loading={isLoading}
        >
          {t('auth.changePassword')}
        </Button>
      </ModalFooter>
    </ModalWrapper>
  );
}
